import {css, StyleSheet} from 'aphrodite';
import React, {useEffect} from 'react';

import {Sequence} from 'components/Sequence';
import {AllPolymerLayouts} from 'constants/MapsConstants';
import FileStates from 'FileStates';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {BioentityPopoverContent} from 'molbio/BioentityPopoverContent';
import {TEMP_SEQUENCE_ID} from 'molbio/MolBioConstants';
import {trackOpenedRNAFromSequenceMap} from 'molbio/SequenceAnalyticsHelper';
import {
  TranscriptionIndicesQuery,
  TranscriptionIndicesQueryVariables,
} from 'molbio/transcriptions/__generated/TranscriptionIndicesQuery';
import {TRANSCRIPTION_INDICES_QUERY} from 'molbio/transcriptions/TranscriptionConstants';
import {
  sliceBoundedTranscriptions,
  TranscriptionIndices,
  SliceBoundedTranscription,
} from 'molbio/TranscriptionsUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import Popover from 'ui-library/floating-ui/Popover';
import sortedBy from 'util/sortedBy';

import ComponentWrapper from './ComponentWrapper';
import {
  TRANSCRIPTION_COLOR,
  FORWARD_TRANSCRIPTION_LINE_Y_POSITION,
  REVERSE_COMPLEMENT_TRANSCRIPTION_LINE_Y_POSITION,
} from './TranscriptionViewConstants';

// ARROW_HEIGHT and ARROW_LENGTH are pixel scale measurements derived from the SVG Path. They should not
// be modified.
// Change ARROW_Y_SCALE and ARROW_X_SCALE to modify the arrow dimensions
const ARROW_Y_SCALE = 1.5;
const ARROW_X_SCALE = 1.15;
const ARROW_HEIGHT = 12 * ARROW_Y_SCALE;
const ARROW_LENGTH = 122 * ARROW_X_SCALE;
const ARROW_PADDING = 5;

type ArrowTypes = 'forward' | 'reverse' | 'spanForward' | 'spanReverse';

const arrowTypeToSVGPath: {[arrowType in ArrowTypes]: JSX.Element} = {
  forward: (
    <g>
      <path d="M113 7L107.75 13.0622V0.937822L113 7Z" />
      <path d="M0 1H108V13H0V1Z" />
    </g>
  ),
  reverse: (
    <g>
      <path d="M0 7L5.25 0.937822L5.25 13.0622L0 7Z" />
      <path d="M5 1H113V13H5V1Z" />
    </g>
  ),
  spanForward: (
    <g>
      <path d="M113 7L107.75 13.0622V0.937822L113 7Z" />
      <path d="M0.0615399 12.9997L6 6.99968L0.0615399 0.999678H108V12.9997H0.0615399ZM0 12.9997H0.0615399L0 13.0619V12.9997ZM0 0.999678H0.0615399L0 0.9375V0.999678Z" />
    </g>
  ),
  spanReverse: (
    <g>
      <path d="M0 7L5.25 0.937822L5.25 13.0622L0 7Z" />
      <path d="M112.938 1.00032L107 7.00032L112.938 13.0003L5 13.0003V1.00032L112.938 1.00032ZM113 1.00032H112.938L113 0.938145V1.00032ZM113 13.0003H112.938L113 13.0625V13.0003Z" />
    </g>
  ),
};

interface TranscriptionWithStartPosition extends SliceBoundedTranscription {
  arrowStartX: number;
}

/**
 * Given an array of transcriptions with start indices, calculate arrow start locations to ensure the
 * arrows don't extend beyond the slice.
 *
 * Arrow locations are measured in pixels
 */
function generateArrowStartX(
  transcriptionsOnSlice: Array<SliceBoundedTranscription>,
  sliceEnd: number,
  xFn: (index: number) => number
): Array<TranscriptionWithStartPosition> {
  const maxArrowStart = Math.max(0, Math.floor(xFn(sliceEnd) - ARROW_LENGTH));
  return transcriptionsOnSlice.map((transcription) => ({
    ...transcription,
    arrowStartX: Math.min(xFn(transcription.clampedStart), maxArrowStart),
  }));
}

interface TranscriptionWithArrowPosition extends TranscriptionWithStartPosition {
  stackIndex: number;
}

/**
 * Given an array of transcriptions, assign stack indices to each so that the arrows for each transcription do
 * not overlap.
 * Overlaps are determined with ARROW_LENGTH as a source of truth. Overlapping arrows are sorted by
 * start index, name, and api_identifier respectively.
 *
 * stack indices are 1-indexed, with lower values starting at the bottom of the layer box height
 */
function generateArrowStackIndices(
  transcriptionsOnSlice: Array<TranscriptionWithStartPosition>
): Array<TranscriptionWithArrowPosition> {
  const sortedTranscriptions = sortedBy(transcriptionsOnSlice, [
    'arrowStartX',
    {accessor: (t) => t.feature.rnaSequence.name, naturalSort: true},
    (t) => t.feature.rnaSequence.id,
  ]);
  const transcriptionsWithStackIndex = [];
  for (let i = 0; i < sortedTranscriptions.length; i++) {
    const curTranscription = sortedTranscriptions[i];
    let j = 0;
    let curStackIndex = 1;
    // Try higher and higher stack indices until we find a placement that doesn't overlap with any arrow
    while (j < i) {
      const previousTranscription = transcriptionsWithStackIndex[j];
      const previousArrowEndX = previousTranscription.arrowStartX + ARROW_LENGTH;
      if (
        curTranscription.arrowStartX < previousArrowEndX &&
        curStackIndex === previousTranscription.stackIndex
      ) {
        j = 0;
        curStackIndex += 1;
      } else {
        j += 1;
      }
    }
    transcriptionsWithStackIndex.push({...curTranscription, stackIndex: curStackIndex});
  }
  return transcriptionsWithStackIndex;
}

function formatArrowDisplayText(text: string): string {
  const maxLength = 15;
  const halfMax = Math.floor(maxLength / 2);
  const textLength = text.length;
  if (text.length > maxLength) {
    return `${text.slice(0, halfMax)}...${text.slice(textLength - halfMax, textLength)}`;
  }
  return text;
}

/**
 * Return text y position relative to the arrow's current y location.
 *
 * Arrow text is oriented with position relative to the containing arrow. When text height is zero, the
 * text box sits on top of the arrow, higher values move the text down.
 */
function getArrowRelativeTextYPos(): number {
  return ARROW_HEIGHT * (3 / 4);
}

/**
 * Return text x position relative to the current horizontal location of the arrow.
 *
 * Arrow text is oriented relative to the containing arrow. When x is zero, the text box starts at arrow's
 * left edge. Higher values move the text to the right.
 *
 * Text is further to the right on reverse and spanning arrows so that text can avoid the left polygon cutouts.
 */
function getArrowRelativeTextXPos(isReverseComplement: boolean, spansStart: boolean): number {
  return isReverseComplement || spansStart ? 8 : 2;
}

interface SVGArrowProps {
  isReverseComplement: boolean;
  spansStart: boolean;
}

function SVGArrow({isReverseComplement, spansStart}: SVGArrowProps): JSX.Element {
  let arrowType: ArrowTypes;
  if (!isReverseComplement && !spansStart) {
    arrowType = 'forward';
  } else if (!isReverseComplement && spansStart) {
    arrowType = 'spanForward';
  } else if (isReverseComplement && !spansStart) {
    arrowType = 'reverse';
  } else {
    arrowType = 'spanReverse';
  }
  return (
    <svg
      fill={TRANSCRIPTION_COLOR}
      {...getTestProps({component: 'TranscriptionArrow', element: 'shape', key: arrowType})}
      data-amp-safe={false}
    >
      {arrowTypeToSVGPath[arrowType]}
    </svg>
  );
}
interface TranscriptionArrowProps {
  transcriptionWithArrowPosition: TranscriptionWithArrowPosition;
  layout: AllPolymerLayouts['transcriptionArrow'];
  arrowXPosition: number;
  indexFromTop: number;
  sliceStart: number;
  setSelection: (start: number, end: number) => void;
}

function TranscriptionArrow({
  transcriptionWithArrowPosition,
  layout,
  arrowXPosition,
  indexFromTop,
  sliceStart,
  setSelection,
}: TranscriptionArrowProps): JSX.Element | null {
  const displayText = transcriptionWithArrowPosition.feature.rnaSequence.name;
  const featureId = transcriptionWithArrowPosition.feature.rnaSequence.id;
  const isReverseComplement = transcriptionWithArrowPosition.feature.isReverseComplement;
  const arrowYPosition = indexFromTop * (ARROW_HEIGHT + ARROW_PADDING) + ARROW_PADDING;
  const spansStart =
    transcriptionWithArrowPosition.clampedStart === sliceStart &&
    transcriptionWithArrowPosition.feature.start !== sliceStart;
  const dataTestAttrs = {component: 'TranscriptionArrow'};
  return (
    <Popover
      renderContent={() => (
        <BioentityPopoverContent
          id={featureId}
          onClickName={() => {
            trackOpenedRNAFromSequenceMap();
            FileStates.get().openFolderItem(featureId);
          }}
          validationExtras={null}
        />
      )}
    >
      <svg
        onClick={() => {
          setSelection(
            transcriptionWithArrowPosition.feature.start,
            transcriptionWithArrowPosition.feature.end
          );
        }}
        {...getTestProps({component: 'TranscriptionArrow', element: 'root', key: featureId})}
        data-amp-safe={false}
      >
        <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={false}>
          <g
            transform={`translate(${arrowXPosition}, ${arrowYPosition})`}
            {...getTestProps({
              component: 'TranscriptionArrow',
              element: 'position',
              key: featureId,
            })}
            data-amp-safe={false}
          >
            <g
              transform={`scale(${ARROW_X_SCALE}, ${ARROW_Y_SCALE})`}
              className={css(styles.transcriptionArrow)}
            >
              <SVGArrow isReverseComplement={isReverseComplement} spansStart={spansStart} />
            </g>
            <text
              x={getArrowRelativeTextXPos(isReverseComplement, spansStart)}
              y={getArrowRelativeTextYPos()}
              className={css(styles.transcriptionArrow)}
              {...getTestProps({...dataTestAttrs, element: 'text'})}
            >
              {formatArrowDisplayText(displayText)}
            </text>
          </g>
        </ComponentWrapper>
      </svg>
    </Popover>
  );
}

interface ConnectorLineProps {
  transcription: TranscriptionWithArrowPosition;
  layout: AllPolymerLayouts['transcriptionArrow'];
  connectorLineX: number;
  indexFromTop: number;
  boxHeightToTranscriptionLine: (reverseComplement: boolean) => number;
}

function ConnectorLine({
  transcription,
  layout,
  connectorLineX,
  indexFromTop,
  boxHeightToTranscriptionLine,
}: ConnectorLineProps): JSX.Element | null {
  const isReverseComplement = transcription.feature.isReverseComplement;
  const arrowDistToTop = ARROW_PADDING + ARROW_HEIGHT + indexFromTop * (ARROW_HEIGHT + ARROW_PADDING);
  // transcription stack indices are 1-indexed
  const arrowDistToBottom = ARROW_PADDING + (transcription.stackIndex - 1) * (ARROW_HEIGHT + ARROW_PADDING);
  const distToTranscriptionLine =
    boxHeightToTranscriptionLine(isReverseComplement) +
    (isReverseComplement
      ? REVERSE_COMPLEMENT_TRANSCRIPTION_LINE_Y_POSITION
      : FORWARD_TRANSCRIPTION_LINE_Y_POSITION);
  // For reverse transcriptions: make line higher to reach revcomp arrow tip
  const lineY1 = arrowDistToTop - (isReverseComplement ? ARROW_HEIGHT / 2 : 0);
  const lineY2 = arrowDistToTop + distToTranscriptionLine + arrowDistToBottom;
  return (
    <svg>
      <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={false}>
        <g>
          <line
            x1={connectorLineX}
            x2={connectorLineX}
            y1={lineY1}
            y2={lineY2}
            stroke={TRANSCRIPTION_COLOR}
            strokeWidth={1.5}
            {...getTestProps({
              component: 'TranscriptionArrow',
              element: 'connectorLine',
              key: transcription.feature.rnaSequence.id,
            })}
            data-amp-safe={false}
          />
        </g>
      </ComponentWrapper>
    </svg>
  );
}

interface TranscriptionArrowStackProps {
  transcriptionsOnSlice: Array<SliceBoundedTranscription>;
  layout: AllPolymerLayouts['transcriptionArrow'];
  redraw: (boxHeight: number) => void;
  sliceEnd: number;
  sliceStart: number;
  boxHeightToTranscriptionLine: (reverseComplement: boolean) => number;
  xFn: (index: number) => number;
  setSelection: (start: number, end: number) => void;
  sequenceVersionIsCurrent: boolean;
}

function TranscriptionArrowStack({
  transcriptionsOnSlice,
  layout,
  redraw,
  sliceEnd,
  sliceStart,
  boxHeightToTranscriptionLine,
  xFn,
  setSelection,
  sequenceVersionIsCurrent,
}: TranscriptionArrowStackProps): JSX.Element | null {
  const transcriptionsWithArrowStart = generateArrowStartX(transcriptionsOnSlice, sliceEnd, xFn);
  const transcriptionsWithArrowPosition = generateArrowStackIndices(transcriptionsWithArrowStart);
  const maxStackIndex = Math.max(
    ...transcriptionsWithArrowPosition.map((transcription) => transcription.stackIndex),
    0 // Avoid negative infinity default if no transcriptions are present
  );
  const isVisible = layout.visible && sequenceVersionIsCurrent;

  useEffect(() => {
    if (isVisible) {
      redraw(maxStackIndex * (ARROW_HEIGHT + ARROW_PADDING) + ARROW_PADDING);
    } else {
      redraw(0);
    }
  }, [isVisible, maxStackIndex, redraw]);

  if (!isVisible) {
    return null;
  }
  return (
    <svg {...getTestProps({component: 'TranscriptionArrowStack', element: 'root'})}>
      <g>
        {transcriptionsWithArrowPosition.map((transcription, i) => (
          <ConnectorLine
            key={`${i}`}
            transcription={transcription}
            layout={layout}
            connectorLineX={xFn(transcription.clampedStart)}
            indexFromTop={maxStackIndex - transcription.stackIndex}
            boxHeightToTranscriptionLine={boxHeightToTranscriptionLine}
          />
        ))}
      </g>
      <g>
        {transcriptionsWithArrowPosition.map((transcription, i) => {
          // Subtract 1 so that arrow always overlaps with line
          const arrowXPosition = transcription.arrowStartX - 1;
          return (
            <TranscriptionArrow
              key={`${i}`}
              transcriptionWithArrowPosition={transcription}
              layout={layout}
              arrowXPosition={arrowXPosition}
              sliceStart={sliceStart}
              indexFromTop={maxStackIndex - transcription.stackIndex}
              setSelection={setSelection}
            />
          );
        })}
      </g>
    </svg>
  );
}

export interface QueryingTranscriptionArrowProps {
  xFn: (index: number) => number;
  sliceStart: number;
  sliceEnd: number;
  sequence: Sequence;
  layout: AllPolymerLayouts['transcriptionArrow'];
  redraw: (boxHeight: number) => void;
  boxHeightToTranscriptionLine: (reverseComplement: boolean) => number;
  setSelection: (start: number, end: number) => void;
}

export function QueryingTranscriptionArrow({
  xFn,
  sliceStart,
  sliceEnd,
  sequence,
  layout,
  redraw,
  boxHeightToTranscriptionLine,
  setSelection,
}: QueryingTranscriptionArrowProps): JSX.Element | null {
  // Some temporary/preview sequences have a temporary ID that breaks graphql serialization.
  if (sequence.id === TEMP_SEQUENCE_ID) {
    return null;
  }
  return (
    <BenchlingQuery<TranscriptionIndicesQuery, TranscriptionIndicesQueryVariables>
      query={TRANSCRIPTION_INDICES_QUERY}
      variables={{sequenceId: sequence.id}}
      renderLoading={({refetch}) => null}
      fetchPolicy="cache-only" // Only reads from live updates triggered in Sequence.tsx
    >
      {(data) => {
        const transcriptionIndices: TranscriptionIndices =
          data.sequence.__typename === 'SequenceNode' ? (data.sequence.transcriptionMatches ?? []) : [];
        const transcriptionsOnSlice = [
          ...sliceBoundedTranscriptions(sliceStart, sliceEnd, false, transcriptionIndices),
          ...sliceBoundedTranscriptions(sliceStart, sliceEnd, true, transcriptionIndices),
        ];
        return (
          <TranscriptionArrowStack
            transcriptionsOnSlice={transcriptionsOnSlice}
            layout={layout}
            redraw={redraw}
            sliceEnd={sliceEnd}
            sliceStart={sliceStart}
            boxHeightToTranscriptionLine={boxHeightToTranscriptionLine}
            xFn={xFn}
            setSelection={setSelection}
            sequenceVersionIsCurrent={sequence.get('currentVersion')}
          />
        );
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  transcriptionArrow: {
    cursor: 'pointer',
  },
});

export {QueryingTranscriptionArrow as TranscriptionArrow};
